import { Card } from '../../../components/ui/Card.js';
import type { DocumentGroup, IdentitySide } from './groups.js';

/** What the platform accepts (`backend/src/modules/users/routes.ts:809-815`). Images outside
 *  it — a HEIC from an iPhone — are re-encoded before submission, so the picker stays open
 *  to any image rather than hiding the customer's own photos from them.
 *
 *  🔴 184: there is deliberately NO `capture` attribute on the input below, and re-adding one
 *  breaks this screen on iOS. `capture` instructs the browser to open a capture device and skip
 *  the picker entirely; iOS Safari obeys literally, so the customer never sees Photo Library or
 *  Choose File and cannot submit a document they already hold — nor a PDF, which this very list
 *  accepts and no camera can produce. 059 shipped `capture="environment"` here and contradicted
 *  the sentence above; 184 reversed it after a customer reported being unable to upload.
 *
 *  The trap is narrow: `capture` is an enumerated attribute whose PRESENCE is the instruction, so
 *  `capture="false"` still reads as a request to capture on some engines. Absence is the only
 *  correct way to say "let the customer choose". Pinned by
 *  `tests/pages/account/identity/DocumentGroupCard.test.tsx`. */
const ACCEPT = 'image/*,application/pdf';

interface DocumentGroupCardProps {
  group: DocumentGroup;
  provided: Record<string, boolean>;
  /** False while impersonating — reads stay, writes are withheld (059 FR-017). */
  canSubmit: boolean;
  busySide?: IdentitySide | null;
  onSelect: (side: IdentitySide, file: File, input: HTMLInputElement) => void;
  onView: (side: IdentitySide) => void;
  /** 186: the marker for this group's heading, or absent for no marker. Computed by the screen from
   *  what the PLATFORM published — never decided here, and never defaulted to a guess. */
  requirementLabel?: string | null;
}

/** One document — its sides, what is held for each, and the controls acting on them.
 *  Props in, callbacks out: every decision about submitting belongs to the screen. */
export function DocumentGroupCard({
  group,
  provided,
  canSubmit,
  busySide,
  onSelect,
  onView,
  requirementLabel,
}: DocumentGroupCardProps) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-text">{group.label}</h2>
        {requirementLabel && (
          <span
            data-testid={`identity-requirement-${group.key}`}
            className="text-xs font-medium text-primary shrink-0"
          >
            {requirementLabel}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {group.sides.map(({ side, label }) => {
          const held = Boolean(provided[side]);
          return (
            <div
              key={side}
              data-testid={`identity-side-${side}`}
              className="flex items-center justify-between gap-3"
            >
              <div>
                {label && <p className="text-sm text-text">{label}</p>}
                <p className="text-xs text-gray-400">{held ? 'Provided' : 'Not provided'}</p>
              </div>
              <div className="flex items-center gap-4">
                {held && (
                  <button
                    type="button"
                    className="text-xs font-medium text-gray-300 hover:text-text"
                    onClick={() => onView(side)}
                  >
                    View
                  </button>
                )}
                {canSubmit && (
                  <label className="cursor-pointer text-xs font-medium text-primary">
                    {held ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      className="sr-only"
                      accept={ACCEPT}
                      disabled={busySide != null}
                      onChange={(e) => {
                        const input = e.currentTarget as HTMLInputElement;
                        const file = input.files?.[0];
                        if (file) onSelect(side, file, input);
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
